import { compose, lifecycle, withState } from 'recompose';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm, Field, formValueSelector, change } from 'redux-form';
import Joi from 'joi-fresh';
import { withRouter } from 'react-router';
import { get, debounce } from 'lodash';
// eslint-disable-next-line max-len
import LockableInputWithIcon from '../../../../common/LockableInputWithIcon';

import withValidatePassword from '../../../../common/hoc/withValidatePassword';
import { syncValidateDataJoiFresh } from '../../../../common/validator';
import BasicFormField from '../../../../common/formFields/basicFormField';
import CancelButtonLink from '../../../../common/buttons/cancelButtonLink';
import AdminSaveButton from '../../../shared/Buttons/AdminSaveButton';
import { getAllBarbersForUserForm } from '../../../shops/reducers';
import { trimFunction } from '../../../../common/trimData';
import Permissions from '../../../../common/permissionsForm/permissions';
import { beforeSave } from '../../helpers/form';
import ShopArray from './shopArray';
import BarberSelect from './barberSelect';
import CheckboxSwitch from '../../../../common/checkboxSwitch';
import LoginButton from './LoginButton';
import AdminDeleteButton from '../../../shared/Buttons/AdminDeleteButton';
import ImpersonateUser from '../../../shared/ImpersonateUser';
import LogoutUserSessions from '../../../shared/LogoutUserSessions';
import ShopType from '../../../shops/components/shopType';
import BrandField from '../../../../main/shops/containers/BrandField';
import { fetchBarberResultsAdmin } from '../../../../main/search/actions';
import { USER_TYPES } from '../../../../auth/constants';
import UserPermissions from './userPermissions';
import { RoleAllowSuperAdmin } from '../../../../auth/roleAware';
import { GLYPHS } from '../../../../common/icon';
import { flagNames, withInjectFeatureFlagsToProps } from '../../../../../modules/infra/feature-flags';

const defaultShopError = 'Shop must be selected';
const schema = Joi.object({
  username: Joi.string().required().min(2).max(50).label('Username'),
  password: Joi.string().allow('', null).max(50).optional().label('New password'),
  repeatPassword: Joi.any()
    .valid(Joi.ref('password'))
    .label('Repeat password')
    .messages({ 'any.only': 'must match password' }),
  optIn2FA: Joi.boolean().optional().allow(null),
  kind: Joi.string(),
  shopIds: Joi.when('kind', {
    is: USER_TYPES.shop.toString(),
    then: Joi.array()
      .items(
        Joi.string().required().messages({
          'string.base': defaultShopError,
        })
      )
      .required()
      .messages({
        'array.includesRequiredUnknowns': defaultShopError,
        'any.required': defaultShopError,
        'array.sparse': defaultShopError,
      }),
    otherwise: Joi.allow('', null),
  }),
  brand: Joi.when('kind', {
    is: USER_TYPES.brand.toString(),
    then: Joi.object({
      id: Joi.string().required().messages({
        'string.base': 'Brand must be selected',
      }),
    }),
    otherwise: Joi.object({
      id: Joi.any().allow(null),
    }).optional(),
  }),
  barbers: Joi.array()
    .when('kind', {
      is: USER_TYPES.barber.toString(),
      then: Joi.required().messages({ 'any.required': 'Barbers must be selected' }),
      otherwise: Joi.allow('', null),
    })
    .optional(),
  enabled: Joi.boolean().optional(),
  setAccountOwner: Joi.boolean().optional(),
  firstName: Joi.string().when('setAccountOwner', {
    is: true,
    then: Joi.required().label('First Name'),
    otherwise: Joi.allow('', null),
  }),
  lastName: Joi.string().when('setAccountOwner', {
    is: true,
    then: Joi.required().label('Last Name'),
    otherwise: Joi.allow('', null),
  }),
})
  .xor('shopIds', 'brand')
  .messages({
    'object.missing': 'Either Shop or Brand is required but not both',
  });

const validate = (values) => syncValidateDataJoiFresh(values, schema);

const renderUserShop = (shop) => (
  <div key={shop.id} className="u-mb-">
    <div className="c-label--small u-fontsize-extrasmall">Name: {shop.name}</div>
    <div className="c-label--small u-fontsize-extrasmall">
      Kind: <ShopType type={shop.kind} />
    </div>
    <div display-if={get(shop, 'shop_users.permissionPasscode')} className="c-label--small u-fontsize-extrasmall">
      Passcode: {shop.shop_users.permissionPasscode}
    </div>
  </div>
);

class UserForm extends Component {
  constructor(props) {
    super(props);

    this.validatePassword = debounce(this.props.validatePasswordForUserKind(props.kindValue), 500);

    this.handleKindChange = () => {
      this.props.dispatch(change('userForm', 'barberId', null));
    };
  }

  componentWillReceiveProps(newProps) {
    if (newProps.kindValue !== this.props.kindValue) {
      this.validatePassword = debounce(this.props.validatePasswordForUserKind(newProps.kindValue), 500);
    }
  }

  saveUser = (data) => {
    const errors = validate(data);

    if (errors.shopIds) {
      this.props.setFormSubmitFailed(true);
    } else {
      const model = beforeSave(data, this.props.edit, this.props.initialValues);
      this.props.saveUpdatedUser(model);
    }
  };

  render() {
    const {
      handleSubmit,
      shops,
      kindValue,
      barbers,
      validatePasswordWarning,
      edit,
      onDeleteClick,
      userId,
      userShops,
      fetchBarberResultsAdmin: dFetchBarberResultsAdminAction,
      initialValues,
      setAccountOwner,
      formSubmitFailed,
      validateShopsError,
      showTwoFaOptIn,
      change: changeField,
    } = this.props;
    const isShopUser = kindValue === USER_TYPES.shop;
    const isBarberUser = kindValue === USER_TYPES.barber;
    const isBrandUser = kindValue === USER_TYPES.brand;
    const isAdminUser = kindValue === USER_TYPES.regularAdmin;
    const isAgentUser = kindValue === USER_TYPES.agent;
    const isAdminOrAgentUser = isAdminUser || isAgentUser;

    return (
      <form className="c-inner-wrapper u-max-width-710" onSubmit={handleSubmit(trimFunction(this.saveUser))}>
        <div className="u-mb+">
          <div className="layout">
            <div className="layout__item u-1/3 u-pl+">
              <CheckboxSwitch name="enabled" id="enabled" label="Enabled" wrapperClass="u-inline-block u-mr" />
            </div>
            <div className="layout__item u-1/3 u-text-right">
              <ImpersonateUser id={userId} display-if={!isAdminOrAgentUser}>
                <LoginButton />
              </ImpersonateUser>
            </div>
            <div className="layout__item u-1/3">
              <LogoutUserSessions id={userId} display-if={edit} />
            </div>
          </div>
        </div>
        <div className="layout__item u-mb--" display-if={userShops.length}>
          <span className="c-label-new">Shops</span>
          <div className="u-height-735 u-scrollable">{userShops.map(renderUserShop)}</div>
        </div>
        <BasicFormField
          display-if={edit}
          placeholder="New password"
          label="New password"
          name="password"
          maxLength="50"
          type="password"
          onChange={this.validatePassword}
        />
        <BasicFormField
          display-if={edit}
          placeholder="Repeat password"
          label="Repeat password"
          name="repeatPassword"
          maxLength="50"
          type="password"
        />
        <div className="u-font-color-red zeta u-ph u--mt u-mb" display-if={edit && validatePasswordWarning}>
          {validatePasswordWarning}
        </div>
        <BasicFormField placeholder="Username" label="Username" name="username" maxLength="50" />
        <div className="layout__item u-1/2-medium-and-up" display-if={showTwoFaOptIn}>
          <div className="c-label-new">Phone</div>
          <LockableInputWithIcon
            name="phone"
            type="text"
            placeholder="Phone"
            hint="This phone is used for 2FA"
            icon={GLYPHS.twoFaKey}
            className="c-input"
            disabled
            showIcon
          />
        </div>
        <div className="layout__item u-1/1" display-if={showTwoFaOptIn}>
          <div className="c-label-new">Email</div>
          <LockableInputWithIcon
            name="email"
            type="text"
            placeholder="Email"
            hint="This email is used for 2FA"
            icon={GLYPHS.twoFaKey}
            className="c-input"
            disabled
            showIcon
          />
        </div>
        <CheckboxSwitch
          display-if={showTwoFaOptIn}
          name="optIn2FA"
          id="optIn2FA"
          wrapperClass="layout__item u-1/2-medium-and-up"
          label="Opt-in for 2-step verification"
          disabled={!initialValues.optIn2FA}
        />
        <div className="layout__item">
          <div className="c-label-new">Kind</div>
          <div>
            <label className="c-radio u-mr-" htmlFor="kind6">
              <Field
                id="kind6"
                name="kind"
                component="input"
                type="radio"
                className="c-radio__input"
                value="6"
                onClick={this.handleKindChange}
              />
              <span className="c-radio__trigger" />
              <span className="c-radio__label">Brand</span>
            </label>
            <label className="c-radio u-mr-" htmlFor="kind2">
              <Field
                id="kind2"
                name="kind"
                component="input"
                type="radio"
                className="c-radio__input"
                value="2"
                onClick={this.handleKindChange}
              />
              <span className="c-radio__trigger" />
              <span className="c-radio__label">Shop &nbsp;</span>
            </label>
            <label className="c-radio u-mr-" htmlFor="kind1">
              <Field
                id="kind1"
                name="kind"
                component="input"
                type="radio"
                className="c-radio__input"
                value="1"
                onClick={this.handleKindChange}
              />
              <span className="c-radio__trigger" />
              <span className="c-radio__label">Barber &nbsp;</span>
            </label>
            <RoleAllowSuperAdmin wrapperClass="c-radio u-mr-">
              <label htmlFor="kind3">
                <Field
                  id="kind3"
                  name="kind"
                  component="input"
                  type="radio"
                  className="c-radio__input"
                  value="4"
                  onClick={this.handleKindChange}
                />
                <span className="c-radio__trigger" />
                <span className="c-radio__label">Admin</span>
              </label>
            </RoleAllowSuperAdmin>
            <RoleAllowSuperAdmin wrapperClass="c-radio u-mr-">
              <label htmlFor="kind5">
                <Field
                  id="kind5"
                  name="kind"
                  component="input"
                  type="radio"
                  className="c-radio__input"
                  value="5"
                  onClick={this.handleKindChange}
                />
                <span className="c-radio__trigger" />
                <span className="c-radio__label">Agent</span>
              </label>
            </RoleAllowSuperAdmin>
          </div>
        </div>
        <UserPermissions display-if={isAdminOrAgentUser} kind={USER_TYPES.regularAdmin} name="adminPermissions" />
        <div className="layout__item u-position-relative" display-if={isShopUser}>
          <div className="c-label-new">Shops: </div>
          <ShopArray shops={shops} hideError />
          {formSubmitFailed && validateShopsError && (
            <div className="c-input-error-message u-ml-30">{validateShopsError}</div>
          )}
        </div>
        <div className="layout__item u-max-width-300" display-if={isBrandUser}>
          <Field
            name="brand.name"
            id="brand.name"
            change={changeField}
            component={BrandField}
            brandIdField="brand.id"
          />
        </div>
        <div className="layout__item" display-if={isBarberUser}>
          <div className="c-label-new">Barbers: </div>
          <BarberSelect barbers={barbers} fetcher={dFetchBarberResultsAdminAction} />
        </div>

        {!isAdminOrAgentUser && (
          <CheckboxSwitch
            name="setAccountOwner"
            id="setAccountOwner"
            disabled={initialValues.setAccountOwner}
            label="Account Owner"
            wrapperClass="layout__item"
          />
        )}

        {setAccountOwner && (
          <>
            <BasicFormField placeholder="First name" label="First name" name="firstName" maxLength="50" />
            <BasicFormField placeholder="Last name" label="Last name" name="lastName" maxLength="50" />
          </>
        )}

        <div display-if={isShopUser || isBrandUser}>
          <CheckboxSwitch
            name="trackEmployeeTime"
            id="trackEmployeeTime"
            label="Track employee time"
            wrapperClass="layout__item"
          />
          <CheckboxSwitch
            name="permissionPasscodeRequired"
            id="permissionPasscodeRequired"
            label="Permission passcode required"
            wrapperClass="layout__item"
          />
          <Permissions shopUser isBrandUser={isBrandUser} />
        </div>
        <div className="c-separator u-mv" />
        <div className="layout">
          <div className="layout__item u-2/6">
            <AdminDeleteButton className="c-btn c-btn--red u-width-120" display-if={edit} onClick={onDeleteClick} />
          </div>
          <div className="layout__item u-4/6 u-text-right">
            <CancelButtonLink link="/admin/users" content="Cancel" />
            <AdminSaveButton content="Save" />
          </div>
        </div>
      </form>
    );
  }
}
UserForm.propTypes = {
  saveUpdatedUser: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
  barbers: PropTypes.array,
  shops: PropTypes.array,
  kindValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dispatch: PropTypes.func.isRequired,
  validatePasswordForUserKind: PropTypes.func.isRequired,
  validatePasswordWarning: PropTypes.string.isRequired,
  validateShopsError: PropTypes.string,
  edit: PropTypes.bool,
  userId: PropTypes.string,
  userShops: PropTypes.array,
  fetchBarberResultsAdmin: PropTypes.func,
  setAccountOwner: PropTypes.bool,
  initialValues: PropTypes.object.isRequired,
  formSubmitFailed: PropTypes.bool.isRequired,
  setFormSubmitFailed: PropTypes.func.isRequired,
  showTwoFaOptIn: PropTypes.bool.isRequired,
  change: PropTypes.func.isRequired,
};

UserForm.defaultProps = {
  kindValue: null,
  onLogoutAllClick: () => {},
  onDeleteClick: () => {},
  edit: false,
  userId: '',
  userShops: [],
  shops: [],
  barbers: [],
  setAccountOwner: false,
  validateShopsError: '',
};

const selector = formValueSelector('userForm');

export default compose(
  withInjectFeatureFlagsToProps([flagNames['2FA_ENABLE'], flagNames['2FA_PHASE_ONE']]),
  withRouter,
  connect((state, ownProps) => ({
    initialValues: {
      ...ownProps.initialValues,
    },
  })),
  withState('formSubmitFailed', 'setFormSubmitFailed', false),
  reduxForm({
    form: 'userForm',
    validate,
    enableReinitialize: true,
    onSubmitFail: (errors, dispatch, submitError, props) => {
      if (errors && errors.shopIds) {
        props.setFormSubmitFailed(true);
      }
    },
  }),
  connect(
    (state, ownProps) => {
      const kindValue = parseInt(selector(state, 'kind'), 10);
      const validateShopsError = state.form?.userForm?.syncErrors?.shopIds;

      return {
        kindValue,
        barbers: getAllBarbersForUserForm(state),
        userId: ownProps.params.id,
        // redux-form misinterprets array-like fields when they are hidden
        validateShopsError: typeof validateShopsError === 'string' ? validateShopsError : defaultShopError,
        setAccountOwner: selector(state, 'setAccountOwner'),
        showTwoFaOptIn:
          ownProps.edit &&
          kindValue === USER_TYPES.barber &&
          ownProps[flagNames['2FA_ENABLE']] &&
          ownProps[flagNames['2FA_PHASE_ONE']],
      };
    },
    {
      fetchBarberResultsAdmin,
    }
  ),
  withValidatePassword
)(UserForm);
